import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { useResponsive } from '../../hooks/useResponsive';
import { VersionTimeline } from './VersionTimeline';
import { VersionDetail } from './VersionDetail';
import { useVersionHistory, Version } from './hooks/useVersionHistory';

interface HistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  objectType: string;
  objectId: string;
}

type ViewMode = 'timeline' | 'detail';

export function HistoryDrawer({
  open,
  onOpenChange,
  objectType,
  objectId,
}: HistoryDrawerProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const { data, isLoading, error } = useVersionHistory(objectType, objectId, {
    limit,
    offset,
    enabled: open,
  });

  const handleViewDetail = (version: Version) => {
    setSelectedVersion(version);
    setViewMode('detail');
  };

  const handleBackToTimeline = () => {
    setSelectedVersion(null);
    setViewMode('timeline');
  };

  const handleLoadMore = () => {
    setOffset((prev) => prev + limit);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={isMobile ? 'w-full' : 'w-[400px] sm:w-[500px]'}
      >
        <SheetHeader className="pb-4">
          <SheetTitle>{t('versioning.history')}</SheetTitle>
          {/* The pointer a user quotes and the PDF stamps. A quiet caption, not a
              badge — the timeline's revision markers carry the emphasis. */}
          {data?.current_revision != null && (
            <p className="text-xs text-muted-foreground">
              {t('versioning.currentRevision', { number: data.current_revision })}
            </p>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {viewMode === 'timeline' && (
            <VersionTimeline
              versions={data?.data ?? []}
              isLoading={isLoading}
              error={error as Error | null}
              hasMore={data?.has_more ?? false}
              onViewDetail={handleViewDetail}
              onLoadMore={handleLoadMore}
            />
          )}

          {viewMode === 'detail' && selectedVersion && (
            <VersionDetail
              version={selectedVersion}
              onBack={handleBackToTimeline}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
